import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CLINICAL_SERVICES } from '../constants/clinicData';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { ArrowLeft, Phone } from 'lucide-react';

const ServiceDetailPage = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const service = CLINICAL_SERVICES.find(s => s.titleKey === serviceId);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">Service Not Found</h1>
          <Button onClick={() => navigate('/')}>Return Home</Button>
        </div>
      </div>
    );
  }

  const Icon = service.icon;

  const getServiceConditions = (serviceKey) => {
    const conditions = {
      diabetes: [
        'Type 1 Diabetes',
        'Type 2 Diabetes',
        'Gestational Diabetes',
        'Pre-diabetes',
        'Diabetic Complications',
        'Blood Sugar Management',
        'HbA1c Control',
        'Insulin Therapy'
      ],
      hypertension: [
        'High Blood Pressure',
        'Stage 1 & 2 Hypertension',
        'Resistant Hypertension',
        'Secondary Hypertension',
        'Hypertensive Emergency',
        'Blood Pressure Monitoring',
        'Cardiac Risk Assessment',
        'Lifestyle Modification'
      ],
      thyroid: [
        'Hypothyroidism',
        'Hyperthyroidism',
        'Thyroid Nodules',
        'Goiter',
        'Thyroid Function Tests',
        'Hormone Replacement Therapy',
        'Autoimmune Thyroid Disease',
        'Thyroid Monitoring'
      ],
      cardiac: [
        'Chest Pain Evaluation',
        'ECG Monitoring',
        'High Cholesterol',
        'Heart Disease Prevention',
        'Cardiac Risk Stratification',
        'Lipid Profile Management',
        'Cardiovascular Assessment',
        'Post-Heart Attack Care'
      ],
      respiratory: [
        'Asthma',
        'COPD (Chronic Obstructive Pulmonary Disease)',
        'Chronic Cough',
        'Breathlessness',
        'Post-COVID Lung Care',
        'Pleural Effusion',
        'Bronchitis',
        'Respiratory Infections'
      ],
      rheumatology: [
        'Rheumatoid Arthritis',
        'Osteoarthritis',
        'Osteoporosis',
        'Gout',
        'Joint Pain',
        'SLE (Lupus)',
        'Autoimmune Disorders',
        'Inflammatory Arthritis'
      ],
      fever: [
        'Viral Fevers',
        'Dengue Fever',
        'Malaria',
        'Typhoid',
        'COVID-19',
        'Acute Infections',
        'Fever of Unknown Origin',
        'Sepsis Management'
      ],
      weightLoss: [
        'Obesity Treatment',
        'Medical Weight Loss',
        'Metabolic Syndrome',
        'Lifestyle Modification',
        'Diet Counseling',
        'Weight Management Programs',
        'BMI Reduction',
        'Preventive Health'
      ],
      stroke: [
        'Acute Stroke Management',
        'Stroke Prevention',
        'TIA (Mini Stroke)',
        'Neurological Emergencies',
        'Post-Stroke Care',
        'Risk Factor Management',
        'Rehabilitation Referral',
        'Secondary Prevention'
      ],
      preventive: [
        'Health Checkups',
        'Adult Vaccinations',
        'Health Screenings',
        'Lifestyle Counseling',
        'Disease Prevention',
        'Risk Assessment',
        'Wellness Programs',
        'Chronic Disease Prevention'
      ],
      emergency: [
        'Emergency Stabilization',
        'Nebulization',
        'ECG Monitoring',
        'Minor Emergency Procedures',
        'Critical Care',
        'Acute Illness Management',
        'Higher Center Referrals',
        'Life-Saving Interventions'
      ],
      family: [
        'Routine Checkups',
        'Preventive Care',
        'Chronic Disease Management',
        'Acute Illness Treatment',
        'Health Counseling',
        'Family Medicine',
        'Geriatric Care',
        'Pediatric Referrals'
      ]
    };

    return conditions[serviceKey] || [
      'Comprehensive evaluation and diagnosis',
      'Evidence-based treatment protocols',
      'Regular monitoring and follow-up',
      'Lifestyle modification guidance'
    ];
  };

  const conditions = getServiceConditions(service.titleKey);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      {/* Hero Section */}
      <div className="relative pt-32 pb-20 px-4" style={{ background: 'linear-gradient(135deg, #053827 0%, #0a5a40 100%)' }}>
        <div className="container mx-auto max-w-6xl">
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="mb-6 text-white border-white hover:bg-white/20"
          >
            <ArrowLeft className="mr-2" size={16} />
            {t('backToHome')}
          </Button>

          <div className="flex items-center gap-6 mb-6">
            <div className="w-24 h-24 rounded-2xl flex items-center justify-center bg-white/10 backdrop-blur-sm">
              <Icon style={{ color: '#ffffff' }} size={48} />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
                {t(`services.${service.titleKey}`)}
              </h1>
              <p className="text-xl text-emerald-100">
                {t('expertCare')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="container mx-auto max-w-6xl px-4 py-16">
        <div className="grid md:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-8">
            {/* Overview */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h2 className="text-3xl font-bold mb-4" style={{ color: 'var(--medical-primary)' }}>
                {t('overview')}
              </h2>
              <p className="text-slate-700 leading-relaxed text-lg">
                {t(`services.${service.descKey}`)}
              </p>
            </div>

            {/* What We Treat */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--medical-primary)' }}>
                {t('conditionsWeTreat')}
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {conditions.map((condition) => (
                  <div key={condition} className="flex items-start gap-3 p-4 rounded-lg bg-slate-50 hover:bg-emerald-50 transition-colors">
                    <div className="w-2 h-2 rounded-full mt-2" style={{ backgroundColor: 'var(--medical-primary)' }}></div>
                    <span className="text-slate-700 font-medium">{condition}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact CTA */}
            <div className="bg-white rounded-2xl p-6 shadow-lg sticky top-24">
              <h3 className="text-2xl font-bold mb-4" style={{ color: 'var(--medical-primary)' }}>
                {t('bookAppointment')}
              </h3>
              <p className="text-slate-700 mb-6">
                {t('bookAppointmentText')}
              </p>
              
              <div className="space-y-3">
                <a 
                  href="tel:+919552374093"
                  className="flex items-center justify-center gap-2 w-full px-6 py-4 text-white font-semibold rounded-lg transition-all duration-300 hover:scale-105 shadow-lg"
                  style={{ backgroundColor: 'var(--medical-primary)' }}
                >
                  <Phone size={20} />
                  {t('callNow')}: +91-9552374093
                </a>

                <a 
                  href="https://www.healthray.online/patient/EOIBIF?id=XXOO&reg=nw"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full px-6 py-4 text-center border-2 font-semibold rounded-lg transition-all duration-300 hover:bg-emerald-50"
                  style={{ borderColor: 'var(--medical-primary)', color: 'var(--medical-primary)' }}
                >
                  {t('bookOnline')}
                </a>
              </div>

              <div className="mt-6 pt-6 border-t">
                <h4 className="font-semibold mb-3 text-slate-900">{t('consultationHours')}</h4>
                <p className="text-slate-600 text-sm">
                  {t('location.hoursValue')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ServiceDetailPage;
